import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchSchwankungen,
  selectSchwankungen,
  selectSchwankungenStatus,
} from 'modules/aktien/features/schwankungenSlice';
import { selectAktie } from 'modules/aktien/features/aktienDbSlice';
import {
  formatKurs,
  formatProz,
  formatDatum,
  makeComparator,
} from 'core/utils/format';
import SortHeader from 'core/components/SortHeader';

/** Schwelle (Prozent), ab der eine Tagesbewegung als Top/Flop gilt. */
const SCHWELLE = 10;

/**
 * Reiter-Definitionen: welche Kennzahl den Reiter fuellt, in welche Richtung
 * gefiltert wird und wie standardmaessig sortiert wird.
 */
const TABS = {
  wochenTops: { key: 'startEnde', positiv: true, label: '📈 Wochen-Tops' },
  wochenFlops: { key: 'startEnde', positiv: false, label: '📉 Wochen-Flops' },
  intradayTops: { key: 'startHoch', positiv: true, label: '📈 Intraday-Tops' },
  intradayFlops: { key: 'startTief', positiv: false, label: '📉 Intraday-Flops' },
};

/** Sortierwert einer Zeile je Spaltenschlüssel. */
const getValue = (s, key) => {
  if (key === 'datum') return s.datum;
  if (key === 'aktienName') return s.aktienName || s.isin;
  if (key === 'startEnde') return s.startEndeProz;
  if (key === 'startHoch') return s.startHochProz;
  if (key === 'startTief') return s.startTiefProz;
  if (key === 'schlusskurs') return s.schlusskurs;
  return null;
};

/** Filtert die Liste auf die Treffer eines Reiters (Kennzahl über/unter der Schwelle). */
const filterTab = (liste, { key, positiv }) => {
  const wertOf = (s) =>
    key === 'startHoch'
      ? s.startHochProz
      : key === 'startTief'
      ? s.startTiefProz
      : s.startEndeProz;
  return liste.filter((s) => {
    const w = Number(wertOf(s));
    return positiv ? w > SCHWELLE : w < -SCHWELLE;
  });
};

/** Reiter-Button der Tops/Flops-Navigation. */
function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? 'border-ink text-ink'
          : 'border-transparent text-ink-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Prozent-Chip: grün bei Plus, rot bei Minus. Beträge über der Schwelle werden
 * kräftig (ring + fett) dargestellt, darunterliegende dezent.
 */
function ProzentChip({ wert }) {
  if (wert == null) {
    return <span className="font-mono tabular-nums text-ink-muted">–</span>;
  }
  const zahl = Number(wert);
  const auffaellig = Math.abs(zahl) > SCHWELLE;
  const farbe =
    zahl >= 0
      ? 'bg-gain/10 text-gain ring-gain/25'
      : 'bg-loss/10 text-loss ring-loss/25';
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 font-mono tabular-nums ring-1 ${farbe} ${
        auffaellig ? 'font-semibold' : 'font-normal opacity-60'
      }`}
    >
      {formatProz(wert)}
    </span>
  );
}

/**
 * News-Ansicht „Schwankungen": Aktien-Tage der letzten Woche mit einer
 * Tagesbewegung von mehr als 10 %. Vier Reiter:
 * Wochen-Tops/-Flops (Tagesstart → Tagesende) und Intraday-Tops/-Flops
 * (Tagesstart → Tageshoch bzw. Tagesstart → Tagestief). Alle Spalten sind
 * sortierbar; Klick auf eine Zeile öffnet die Detailansicht der Aktie.
 */
export default function SchwankungenView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const liste = useSelector(selectSchwankungen);
  const status = useSelector(selectSchwankungenStatus);

  const [tab, setTab] = useState('wochenTops');
  const [sortKey, setSortKey] = useState('datum');
  const [sortDir, setSortDir] = useState('desc');

  // Bei jedem Aufruf neu laden (frische Tageswerte des aktuellen Fensters).
  useEffect(() => {
    dispatch(fetchSchwankungen());
  }, [dispatch]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Text aufsteigend, Zahlen/Datum zuerst absteigend (größte/neueste oben).
      setSortDir(key === 'aktienName' ? 'asc' : 'desc');
    }
  };

  const treffer = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(TABS).map(([name, cfg]) => [name, filterTab(liste, cfg)])
      ),
    [liste]
  );

  const sichtbar = useMemo(() => {
    const basis = treffer[tab] ?? [];
    return [...basis].sort(makeComparator((row) => getValue(row, sortKey), sortDir));
  }, [tab, treffer, sortKey, sortDir]);

  const handleOpen = (isin) => {
    dispatch(selectAktie(isin));
    navigate(`/projekte/aktienanalyse/aktie/${isin}`);
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Schwankungen</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Aktien mit einer Tagesbewegung von mehr als {SCHWELLE} % in der letzten
          Woche – Tagesstart zu Tagesende (Wochen-Tops/-Flops) sowie Tagesstart zu
          Tageshoch bzw. Tagestief (Intraday-Tops/-Flops).
        </p>
      </header>

      {status === 'loading' && <p className="text-ink-muted">Lade Schwankungen…</p>}
      {status === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Die Schwankungen konnten nicht geladen werden.
        </p>
      )}

      {status === 'succeeded' && (
        <>
          {/* Tabs: Wochen- und Intraday-Tops/-Flops */}
          <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
            {Object.entries(TABS).map(([name, cfg]) => (
              <TabButton
                key={name}
                active={tab === name}
                onClick={() => setTab(name)}
              >
                {cfg.label} ({treffer[name]?.length ?? 0})
              </TabButton>
            ))}
          </div>

          {sichtbar.length === 0 ? (
            <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
              Keine Aktie in dieser Kategorie über {SCHWELLE} %.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
                    <SortHeader
                      label="Datum"
                      columnKey="datum"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Aktie"
                      columnKey="aktienName"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Start → Ende"
                      columnKey="startEnde"
                      align="right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Start → Hoch"
                      columnKey="startHoch"
                      align="right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Start → Tief"
                      columnKey="startTief"
                      align="right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Schlusskurs"
                      columnKey="schlusskurs"
                      align="right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sichtbar.map((s) => (
                    <tr
                      key={`${s.isin}-${s.datum}`}
                      onClick={() => handleOpen(s.isin)}
                      className="cursor-pointer border-t border-line transition-colors hover:bg-ink/[0.03]"
                    >
                      <td className="px-4 py-3 text-ink-soft">{formatDatum(s.datum)}</td>
                      <td className="px-4 py-3">
                        <div className="truncate font-medium text-ink">
                          {s.aktienName || s.isin}
                        </div>
                        <div className="font-mono text-xs text-ink-muted">{s.isin}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ProzentChip wert={s.startEndeProz} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ProzentChip wert={s.startHochProz} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ProzentChip wert={s.startTiefProz} />
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                        {formatKurs(s.schlusskurs, s.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {sichtbar.length > 0 && (
            <p className="mt-3 font-mono text-xs text-ink-muted">
              {sichtbar.length} Treffer
            </p>
          )}
        </>
      )}
    </div>
  );
}
